import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  TbAlertTriangle,
  TbCheck,
  TbChevronDown,
  TbChevronUp,
  TbClock,
  TbDotsVertical,
  TbX,
} from 'react-icons/tb';

import { DateSelection } from '../../types/shared/Vendor';
import { Market } from '../../types/shared/Market';
import formatDate from '../../core/support/formatDate';
import { DateStatus } from '../../runtypes/VendorRuntype';

type Props = {
  market: Market;
  dates: DateSelection;
  onChange: (dates: DateSelection) => void;
};

export function MarketEditView(props: Props) {
  const { market, dates, onChange } = props;
  const shouldShowMarket = market.dates.some((date) => shouldShow(dates[date]));
  const approvedDates = market.dates.filter((date) => isApproved(dates[date]));
  const count = approvedDates.length;
  const total = market.dates.length;
  const [isOpen, setOpen] = useState(shouldShowMarket);
  const [selectedDates, setSelectedDates] = useState(() => new Set<string>());
  return (
    <VStack align="stretch" spacing={0}>
      <HStack
        as="a"
        href="#"
        paddingY={3}
        paddingX={4}
        backgroundColor="gray.50"
        borderWidth={1}
        borderColor="gray.200"
        borderTopLeftRadius={6}
        borderTopRightRadius={6}
        onClick={(event) => {
          event.preventDefault();
          setOpen(!isOpen);
        }}
      >
        <VStack align="stretch" spacing={1}>
          <Text fontWeight="medium">{market.name}</Text>
          <Text color="gray.400">
            {t('{count} of {total} date(s) approved', { count, total })}
          </Text>
        </VStack>
        <Spacer />
        <Icon as={isOpen ? TbChevronUp : TbChevronDown} fontSize={20} />
      </HStack>
      <Collapse in={isOpen}>
        <VStack
          align="stretch"
          padding={4}
          borderLeftWidth={1}
          borderRightWidth={1}
          borderBottomWidth={1}
          borderColor="gray.200"
          borderBottomLeftRadius={6}
          borderBottomRightRadius={6}
        >
          <BulkActions
            allDates={market.dates}
            dates={dates}
            selectedDates={selectedDates}
            onSelectionChange={(dates) => setSelectedDates(dates)}
            onChangeStatus={(newStatus) => {
              const newDates = { ...dates };
              for (const date of selectedDates) {
                if (newStatus == null) {
                  delete newDates[date];
                } else {
                  newDates[date] = newStatus;
                }
              }
              onChange(newDates);
              setSelectedDates(new Set());
            }}
          />
          <HStack flexWrap="wrap" spacing={0} gap={2}>
            {market.dates.map((date) => (
              <Date
                key={date}
                marketId={market.id}
                date={date}
                status={dates[date]}
                isChecked={selectedDates.has(date)}
                onToggle={() => {
                  const newSelectedDates = new Set(selectedDates);
                  newSelectedDates.has(date)
                    ? newSelectedDates.delete(date)
                    : newSelectedDates.add(date);
                  setSelectedDates(newSelectedDates);
                }}
                onStatusChange={(date, newStatus) => {
                  const newDates = { ...dates };
                  if (newStatus == null) {
                    delete newDates[date];
                  } else {
                    newDates[date] = newStatus;
                  }
                  onChange(newDates);
                }}
              />
            ))}
          </HStack>
        </VStack>
      </Collapse>
    </VStack>
  );
}

function BulkActions(props: {
  allDates: Array<string>;
  dates: DateSelection;
  selectedDates: Set<string>;
  onSelectionChange: (dates: Set<string>) => void;
  onChangeStatus: (newStatus: DateStatus | undefined) => void;
}) {
  const {
    allDates,
    dates,
    selectedDates,
    onSelectionChange,
    onChangeStatus,
  } = props;
  const count = selectedDates.size;
  const noneChecked = count === 0;
  const someChecked = count > 0;
  const allChecked = count === allDates.length;
  return (
    <HStack>
      <Checkbox
        isChecked={someChecked}
        isIndeterminate={someChecked && !allChecked}
        onChange={() => {
          let newSelection: Set<string>;
          // This makes this a sort of three-way toggle.
          // - If none selected, select all except not-attending
          // - If some, but not all selected, select all
          // - If all currently selected, select none
          if (noneChecked) {
            const datesToSelect = allDates.filter(
              (date) => dates[date] !== undefined,
            );
            newSelection = new Set(
              datesToSelect.length ? datesToSelect : allDates,
            );
          } else if (allChecked) {
            newSelection = new Set();
          } else {
            newSelection = new Set(allDates);
          }
          onSelectionChange(newSelection);
        }}
      >
        {t('{count} date(s) selected', { count })}
      </Checkbox>
      <Spacer />
      {someChecked ? (
        <HStack spacing={3}>
          <Button
            color="green.400"
            variant="link"
            textDecoration="none"
            leftIcon={<Icon as={TbCheck} fontSize={17} mt="1px" />}
            iconSpacing={1}
            onClick={() => onChangeStatus('APPROVED')}
          >
            {t('Add/Approve')}
          </Button>
          <Button
            color="blue.400"
            variant="link"
            textDecoration="none"
            leftIcon={<Icon as={TbClock} fontSize={17} mt="1px" />}
            iconSpacing={1}
            onClick={() => onChangeStatus('WAITLISTED')}
          >
            {t('Waitlist')}
          </Button>
          <Button
            color="red.400"
            variant="link"
            textDecoration="none"
            leftIcon={<Icon as={TbX} fontSize={17} mt="1px" />}
            iconSpacing={1}
            onClick={() => onChangeStatus(undefined)}
          >
            {t('Remove')}
          </Button>
        </HStack>
      ) : null}
    </HStack>
  );
}

function Date(props: {
  marketId: string;
  date: string;
  status: DateStatus | undefined;
  isChecked: boolean;
  onToggle: () => void;
  onStatusChange: (date: string, status: DateStatus | undefined) => void;
}) {
  const { marketId, date, status, isChecked, onToggle, onStatusChange } = props;
  return (
    <HStack
      align="flex-start"
      borderWidth={1}
      borderColor="gray.200"
      borderRadius={3}
    >
      <Checkbox
        name={marketId}
        value={date}
        isChecked={isChecked}
        onChange={onToggle}
        minW={160}
        padding={2}
      >
        <VStack as="span" align="flex-start" spacing={0}>
          <Text as="span">{formatDate(date, { fromUTC: true })}</Text>
          <DateStatusText status={status} />
        </VStack>
      </Checkbox>
      <DateMenu date={date} status={status} onStatusChange={onStatusChange} />
    </HStack>
  );
}

function DateStatusText(props: { status: DateStatus | undefined }) {
  const { status } = props;
  switch (status) {
    case 'REQUESTED': {
      return (
        <HStack as="span" spacing={1} color="orange.400">
          <Icon as={TbAlertTriangle} fontSize={14} />
          <Text as="span">{t('Requested')}</Text>
        </HStack>
      );
    }
    case 'APPROVED': {
      return (
        <HStack as="span" spacing={1} color="green.400">
          <Icon as={TbCheck} fontSize={14} />
          <Text as="span">{t('Approved')}</Text>
        </HStack>
      );
    }
    case 'WAITLISTED': {
      return (
        <HStack as="span" spacing={1} color="blue.400">
          <Icon as={TbClock} fontSize={14} />
          <Text as="span">{t('Waitlisted')}</Text>
        </HStack>
      );
    }
    default: {
      return (
        <HStack as="span" spacing={1} color="gray.400">
          <Text as="span">{t('Not Attending')}</Text>
        </HStack>
      );
    }
  }
}

function DateMenu(props: {
  date: string;
  status: DateStatus | undefined;
  onStatusChange: (date: string, status: DateStatus | undefined) => void;
}) {
  const { date, status, onStatusChange } = props;
  return (
    <Box p={2}>
      <Menu>
        <MenuButton
          as={IconButton}
          size="xs"
          variant="ghost"
          color="gray.400"
          borderRadius={3}
          _hover={{ color: 'black', backgroundColor: 'gray.100' }}
          aria-label={t('Options')}
          icon={<Icon as={TbDotsVertical} fontSize={14} />}
        />
        <Portal>{renderMenuList(date, status, onStatusChange)}</Portal>
      </Menu>
    </Box>
  );
}

function renderMenuList(
  date: string,
  status: DateStatus | undefined,
  onStatusChange: (date: string, status: DateStatus | undefined) => void,
) {
  const iconApprove = (
    <Icon as={TbCheck} color="green.400" fontSize={16} mt={1} />
  );
  const iconRemove = <Icon as={TbX} color="red.400" fontSize={16} mt={1} />;
  const iconWaitlist = (
    <Icon as={TbClock} color="blue.400" fontSize={16} mt={1} />
  );
  const onApprove = () => onStatusChange(date, 'APPROVED');
  const onDecline = () => onStatusChange(date, undefined);
  const onWaitlist = () => onStatusChange(date, 'WAITLISTED');
  switch (status) {
    case 'REQUESTED': {
      return (
        <MenuList>
          <MenuItem align="center" icon={iconApprove} onClick={onApprove}>
            {t('Approve')}
          </MenuItem>
          <MenuItem align="center" icon={iconRemove} onClick={onDecline}>
            {t('Decline')}
          </MenuItem>
          <MenuItem align="center" icon={iconWaitlist} onClick={onWaitlist}>
            {t('Waitlist')}
          </MenuItem>
        </MenuList>
      );
    }
    case 'APPROVED': {
      return (
        <MenuList>
          <MenuItem align="center" icon={iconRemove} onClick={onDecline}>
            {t('Remove')}
          </MenuItem>
          <MenuItem align="center" icon={iconWaitlist} onClick={onWaitlist}>
            {t('Move to Waitlist')}
          </MenuItem>
        </MenuList>
      );
    }
    case 'WAITLISTED': {
      return (
        <MenuList>
          <MenuItem align="center" icon={iconApprove} onClick={onApprove}>
            {t('Approve')}
          </MenuItem>
          <MenuItem align="center" icon={iconRemove} onClick={onDecline}>
            {t('Remove')}
          </MenuItem>
        </MenuList>
      );
    }
    default: {
      return (
        <MenuList>
          <MenuItem align="center" icon={iconApprove} onClick={onApprove}>
            {t('Approve')}
          </MenuItem>
          <MenuItem align="center" icon={iconWaitlist} onClick={onWaitlist}>
            {t('Waitlist')}
          </MenuItem>
        </MenuList>
      );
    }
  }
}

function isApproved(status: DateStatus | undefined) {
  return status === 'APPROVED';
}

// Does a date with this status contribute to if we should show the market expanded
function shouldShow(status: DateStatus | undefined) {
  switch (status) {
    case 'REQUESTED':
    case 'APPROVED':
    case 'WAITLISTED': {
      return true;
    }
    default: {
      return false;
    }
  }
}
